import { CloseInteractionPayload, FormFieldConfig, InteractionClosureType, Template, } from "./shared-interfaces";

export const interactionClosureTypeOptions = [
  { id: "inbound", name: "Inbound" },
  { id: "outbound", name: "Outbound" },
];

export function isTransferWithinTemplate(template?: Template): boolean {
  if (!template) {
    return false;
  }

  return [template.name, template.key]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .includes("transfer within");
}

export function hasCustomCommentPlaceholder(template?: Template): boolean {
  return !!template?.body?.includes("{custom_comment}");
}

interface BuildCloseInteractionFormFieldsArgs {
  closureType: InteractionClosureType | "";
  outboundTemplates: Template[];
  selectedTemplate?: Template;
  onClosureTypeSelect: (value: string) => void;
  onTemplateSelect: (value: string) => void;
  onTransferRequestIdChange: (value?: number | null) => void;
  onCustomCommentChange: (value: string) => void;
  onOverrideContactChange: (value: string) => void;
  onSubmit: () => Promise<void>;
}

export function buildCloseInteractionFormFields({
  closureType,
  outboundTemplates,
  selectedTemplate,
  onClosureTypeSelect,
  onTemplateSelect,
  onTransferRequestIdChange,
  onCustomCommentChange,
  onOverrideContactChange,
  onSubmit,
}: BuildCloseInteractionFormFieldsArgs): FormFieldConfig[] {
  const fields: FormFieldConfig[] = [
    {
      id: "closureType",
      name: "closureType",
      inputType: "select",
      rule: "string",
      label: "Closure Type",
      placeholder: "Select closure type",
      required: true,
      selectOptions: interactionClosureTypeOptions,
      selectHandler: onClosureTypeSelect,
      colConfig: { span: 24 },
    },
    {
      id: "sendSurvey",
      name: "sendSurvey",
      inputType: "checkbox",
      rule: "boolean",
      value: "Send customer survey upon closure",
      required: false,
      initialValue: true,
      valuePropName: "checked",
      colConfig: { span: 24 },
    },
  ];

  if (closureType === "outbound" && outboundTemplates.length > 0) {
    fields.push({
      id: "templateId",
      name: "templateId",
      inputType: "select",
      rule: "string",
      label: "Select Outbound Notification Template",
      placeholder: "Search outbound templates",
      required: true,
      selectOptions: outboundTemplates.map((template) => ({
        id: template.id,
        name: template.name,
      })),
      selectHandler: onTemplateSelect,
      colConfig: { span: 24 },
      elementStyle: { width: "100%" },
    });

    if (isTransferWithinTemplate(selectedTemplate)) {
      fields.push({
        id: "transferRequestId",
        name: "transferRequestId",
        inputType: "number",
        rule: "integer",
        label: "Transfer Request ID",
        placeholder: "Enter transfer request ID",
        required: true,
        min: 1,
        onNumberChange: onTransferRequestIdChange as (value: 0) => void,
        elementStyle: { width: "100%" },
        colConfig: { span: 24 },
      });
    }

    if (hasCustomCommentPlaceholder(selectedTemplate)) {
      fields.push({
        id: "customComment",
        name: "customComment",
        inputType: "textArea",
        rule: "string",
        label: "Custom Message",
        placeholder: "Enter the custom message to include in the notification",
        required: true,
        autoSize: { minRows: 3, maxRows: 6 },
        changeHandler: onCustomCommentChange,
        colConfig: { span: 24 },
      });
    }

    fields.push({
      id: "recipientOverride",
      name: "recipientOverride",
      inputType: "string",
      rule: "string",
      label: "Send Notification To (Optional)",
      placeholder: "Override default contact — enter email or phone number",
      required: false,
      changeHandler: onOverrideContactChange,
      colConfig: { span: 24 },
    });
  }

  if (
    closureType === "inbound" ||
    (closureType === "outbound" && outboundTemplates.length > 0)
  ) {
    fields.push({
      name: "submit",
      value: "Submit",
      inputType: "button",
      required: false,
      className: "base-button",
      handleClick: onSubmit,
      elementStyle: { marginTop: "8px" },
      colConfig: { span: 24 },
    });
  }

  return fields;
}

interface BuildCloseCompanyInteractionFormFieldsArgs {
  closureType: InteractionClosureType | "";
  outboundTemplates: Template[];
  selectedTemplate?: Template;
  onClosureTypeSelect: (value: string) => void;
  onTemplateSelect: (value: string) => void;
  onSubmit: () => Promise<void>;
}

export function buildCloseCompanyInteractionFormFields({
  closureType,
  outboundTemplates,
  selectedTemplate,
  onClosureTypeSelect,
  onTemplateSelect,
  onSubmit,
}: BuildCloseCompanyInteractionFormFieldsArgs): FormFieldConfig[] {
  const fields: FormFieldConfig[] = [
    {
      id: "closureType",
      name: "closureType",
      inputType: "select",
      rule: "string",
      label: "Closure Type",
      placeholder: "Select closure type",
      required: true,
      selectOptions: interactionClosureTypeOptions,
      selectHandler: onClosureTypeSelect,
      colConfig: { span: 24 },
    },
    {
      id: "sendSurvey",
      name: "sendSurvey",
      inputType: "checkbox",
      rule: "boolean",
      value: "Send company survey upon closure",
      required: false,
      initialValue: false,
      valuePropName: "checked",
      colConfig: { span: 24 },
    },
  ];

  if (closureType === "outbound" && outboundTemplates.length > 0) {
    fields.push({
      id: "templateId",
      name: "templateId",
      inputType: "select",
      rule: "string",
      label: "Select Outbound Notification Template",
      placeholder: "Search outbound templates",
      required: true,
      selectOptions: outboundTemplates.map((template) => ({
        id: template.id,
        name: `${template.name}`,
      })),
      selectHandler: onTemplateSelect,
      colConfig: { span: 24 },
      elementStyle: { width: "100%" },
    });

    if (hasCustomCommentPlaceholder(selectedTemplate)) {
      fields.push({
        id: "customComment",
        name: "customComment",
        inputType: "textArea",
        rule: "string",
        label: "Custom Message",
        placeholder: "Enter the custom message to include in the notification",
        required: true,
        autoSize: { minRows: 3, maxRows: 6 },
        colConfig: { span: 24 },
      });
    }
  }

  if (
    closureType === "inbound" ||
    (closureType === "outbound" && outboundTemplates.length > 0)
  ) {
    fields.push({
      name: "submit",
      value: "Submit",
      inputType: "button",
      required: false,
      className: "base-button",
      handleClick: onSubmit,
      elementStyle: { marginTop: "8px" },
      colConfig: { span: 24 },
    });
  }

  return fields;
}

export function buildCloseInteractionPayload(
  values: CloseInteractionPayload,
): CloseInteractionPayload {
  return {
    closureType: values.closureType,
    templateId: values.templateId || undefined,
    transferRequestId: values.transferRequestId || undefined,
    sendSurvey: values.sendSurvey !== false,
    customComment: values.customComment || undefined,
    recipientOverride: values.recipientOverride || undefined,
  };
}

export function buildCloseCompanyInteractionPayload(
  values: CloseInteractionPayload,
): CloseInteractionPayload {
  return {
    closureType: values.closureType,
    templateId: values.templateId || undefined,
    sendSurvey: values.sendSurvey === true,
    customComment: values.customComment || undefined,
  };
}
